import { typedBoolean } from '@shared/helpers'
import { PaperContainer } from '@ui-components/containers/PaperContainer'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { StandardShadow } from '@ui-components/global/shadow'
import { SPACE_SMALL, SPACE_XSMALL, SPACE_XXXXLARGE } from '@ui-components/global/spacing'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { BodyText } from '@ui-components/typography/Typography'
import * as React from 'react'
import { styled } from 'styled-components'

import { EmojiType } from '../assets/Emojis'
import { InfoIcon } from '../assets/Icons'
import { Colors } from '../global/colors'
import { LogoIconType } from '../global/ComponentProps'
import { BREAKPOINT_LARGE_DESKTOP_PX } from '../global/constants'
import { ChangeLanguageButtonOptions, PrimaryTopBarVariantOptions } from '../navigation/PrimaryTopBar'
import { Assistant } from './Assistant'
import { Footer } from './Footer'
import { LargeScreenContainer } from './LargeScreenContainer'
import { WizardHeader } from './WizardHeader'
import { WizardHeaderButtonProps, WizardPageSubHeaderProps } from './WizardTypes'

export interface AssistantStepProps {
  stepId: string
  icon?: LogoIconType
  emoji?: EmojiType
  title?: string
  topic: string
  secondaryHeadingHeader?: string
  primaryHeadingLeadText?: string
  primaryHeadingAdditionalText?: string
  text?: React.ReactNode[]
  topBarVariant?: PrimaryTopBarVariantOptions
  headerButtonProps: WizardHeaderButtonProps
  changeLanguageButtonOptions?: ChangeLanguageButtonOptions
  notification?: string
  children?: React.ReactNode
  subHeaderOptions?: WizardPageSubHeaderProps
  shouldUsePaperContainer?: boolean
  twoColumnLayoutSupport?: boolean
  fullViewportHeight?: boolean
  footer?: React.ReactNode
}

const Wrapper = styled.div`
  @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
    padding-bottom: ${SPACE_XXXXLARGE};
  }
`

const Main = styled.main`
  ${StandardShadow};
  margin-top: ${SPACE_XSMALL};
`

const PaperContainerWrapper = styled.div`
  color: ${Colors.TextDefaultNeutral};
`

const NotificationWrapper = styled.div`
  display: flex;
  gap: ${SPACE_SMALL};
`

const NotificationArea = styled.div`
  color: ${Colors.TextInfo};
`

export const AssistantStep = ({
  title,
  topic,
  icon = LogoIconType.SPankki,
  emoji = 'heyThere',
  topBarVariant = 'large',
  secondaryHeadingHeader,
  primaryHeadingLeadText,
  primaryHeadingAdditionalText,
  text,
  notification,
  children,
  headerButtonProps,
  changeLanguageButtonOptions,
  subHeaderOptions,
  stepId,
  shouldUsePaperContainer = true,
  twoColumnLayoutSupport = false,
  fullViewportHeight = false,
  footer,
}: AssistantStepProps) => {
  const textArray = (Array.isArray(text) ? text : [text]).filter(typedBoolean)
  const leadTexts = textArray.map((paragraph, i) => <p key={`text-${i}`}>{paragraph}</p>)

  return (
    <Wrapper>
      <WizardHeader
        shouldShowMainHeader={true}
        title={title}
        icon={icon}
        hideBoxShadow={true}
        headerButtonProps={headerButtonProps}
        changeLanguageButtonOptions={changeLanguageButtonOptions}
        subHeaderOptions={subHeaderOptions}
        topBarVariant={topBarVariant}
      />
      <Main>
        <Assistant
          data-testid={stepId}
          title={topic}
          emoji={emoji}
          twoColumnLayoutSupport={twoColumnLayoutSupport}
          leadText={primaryHeadingLeadText}
          additionalText={primaryHeadingAdditionalText}
          fullViewportHeight={fullViewportHeight}
        >
          <TightSectionContent>
            {shouldUsePaperContainer ? (
              <PaperContainerWrapper>
                <PaperContainer $largeScreenSupport={true}>
                  <LargeScreenContainer>
                    {secondaryHeadingHeader && (
                      <SecondaryHeading header={secondaryHeadingHeader} leadText={leadTexts} />
                    )}
                    {children}
                  </LargeScreenContainer>
                </PaperContainer>
              </PaperContainerWrapper>
            ) : (
              <div>{children}</div>
            )}
            {notification && (
              <NotificationWrapper>
                <InfoIcon style={{ color: Colors.ElementInformation }} />
                <NotificationArea>
                  <BodyText>{notification}</BodyText>
                </NotificationArea>
              </NotificationWrapper>
            )}
          </TightSectionContent>
        </Assistant>
      </Main>
      <Footer>{footer}</Footer>
    </Wrapper>
  )
}
